import { Mutations, Resources } from '@erecruit/types';
import { createSelector, createSlice, PayloadAction } from 'redux-starter-kit';

import { selectAppState } from '../../../App/state/selectors';

import {
  Attachments,
  Credentials,
  Details,
  MatchInfoSections,
  MatchRecordSections,
  ResumeData,
  SaveMatchRecordPayloadProps,
} from '../types';

export interface MatchRecordState {
  error?: string;
  isLoadingInfo: boolean;
  isLoadingRecord: boolean;
  isUpdating: boolean;
  isLoadingResume: boolean;
  resumeData?: ResumeData;
  infoSections?: MatchInfoSections;
  sections?: MatchRecordSections;
  rejectReasons?: Resources.MatchEndReason[];
  credentials: Credentials &
    Details &
    Attachments & { isLoadingAttachmentsDowload: boolean };
}

const initialState: MatchRecordState = {
  isLoadingInfo: false,
  isLoadingRecord: false,
  isUpdating: false,
  isLoadingResume: false,
  credentials: {
    isLoading: false,
    data: [],
    isLoadingDetails: false,
    details: [],
    isLoadingAttachments: false,
    attachments: [],
    isLoadingAttachmentsDowload: false,
  },
};

const MatchRecordSlice = createSlice({
  slice: 'matchRecord',
  initialState: initialState,
  reducers: {
    approveApprovalPath(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<Mutations.ApproveApprovalPathParams>,
    ) {},
    approveApprovalPathSuccess() {},
    approveApprovalPathFail(state, { payload: error }: PayloadAction<string>) {
      state.error = error;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchMatchInfo(state, { payload }: PayloadAction<number>) {
      state.isLoadingInfo = true;
      state.error = undefined;
    },
    fetchMatchInfoSuccess(
      state,
      {
        payload: { infoSections },
      }: PayloadAction<{
        infoSections: MatchInfoSections;
      }>,
    ) {
      state.infoSections = infoSections;
      state.isLoadingInfo = false;
    },
    fetchMatchInfoFail(state, { payload }: PayloadAction<string>) {
      state.isLoadingInfo = false;
      state.error = payload;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchMatchRecord(state, { payload }: PayloadAction<number>) {
      state.isLoadingRecord = true;
      state.error = undefined;
    },
    fetchMatchRecordSuccess(
      state,
      {
        payload: { sections, rejectReasons },
      }: PayloadAction<{
        sections: MatchRecordSections;
        rejectReasons: Resources.MatchEndReason[];
      }>,
    ) {
      state.sections = sections;
      state.rejectReasons = rejectReasons;
      state.isLoadingRecord = false;
    },
    fetchMatchRecordFail(state, { payload }: PayloadAction<string>) {
      state.isLoadingRecord = false;
      state.error = payload;
    },
    matchAction(
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<{
        matchId: number;
        matchActionType: Resources.MatchActionType;
        note?: string;
        reasonId?: number;
        matchCandidateName?: string;
      }>,
    ) {
      state.isUpdating = true;
      state.error = undefined;
    },
    matchActionSuccess(state) {
      state.isUpdating = false;
    },
    matchActionFail(state, { payload }: PayloadAction<string>) {
      state.isUpdating = false;
      state.error = payload;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchMatchResume(state, { payload }: PayloadAction<number>) {
      state.resumeData = undefined;
      state.isLoadingResume = true;
    },
    fetchMatchResumeSuccess(state, { payload }: PayloadAction<ResumeData>) {
      state.resumeData = payload;
      state.isLoadingResume = false;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchMatchResumeFail(state, { payload }: PayloadAction<string>) {
      state.isLoadingResume = false;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchCredentials(state, { payload }: PayloadAction<number>) {
      state.credentials.isLoading = true;
    },
    fetchCredentialsSuccess(
      state,
      { payload: { data } }: PayloadAction<Pick<Credentials, 'data'>>,
    ) {
      state.credentials.isLoading = false;
      state.credentials.data = data;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchCredentialsFail(state, { payload }: PayloadAction<string>) {
      state.credentials.isLoading = false;
      //   state.error = payload;
    },
    fetchDetails(
      state,
      {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        payload,
      }: PayloadAction<Resources.MatchRequirementProgress['requirementId']>,
    ) {
      state.credentials.isLoadingDetails = true;
    },
    fetchDetailsSuccess(
      state,
      { payload: { details } }: PayloadAction<Pick<Details, 'details'>>,
    ) {
      state.credentials.isLoadingDetails = false;
      state.credentials.details = details;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchDetailsFail(state, { payload }: PayloadAction<string>) {
      state.credentials.isLoadingDetails = false;
      //   state.error = payload;
    },
    fetchAttachments(
      state,
      {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        payload,
      }: PayloadAction<Resources.MatchRequirementProgress['requirementId']>,
    ) {
      state.credentials.isLoadingAttachments = true;
    },
    fetchAttachmentsSuccess(
      state,
      {
        payload: { attachments },
      }: PayloadAction<Pick<Attachments, 'attachments'>>,
    ) {
      state.credentials.isLoadingAttachments = false;
      state.credentials.attachments = attachments;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchAttachmentsFail(state, { payload }: PayloadAction<string>) {
      state.credentials.isLoadingAttachments = false;
      //   state.error = payload;
    },
    downloadAllAttachments(
      state,
      {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        payload,
      }: PayloadAction<
        Mutations.ZipRequirementAttachmentsParams['requirementIds']
      >,
    ) {
      state.credentials.isLoadingAttachmentsDowload = true;
    },
    downloadAllAttachmentsSuccess(state) {
      state.credentials.isLoadingAttachments = false;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    downloadAllAttachmentsFail(state, { payload }: PayloadAction<string>) {
      state.credentials.isLoadingAttachments = false;
      //   state.error = payload;
    },
    rejectApprovalPath(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<Mutations.RejectApprovalPathParams>,
    ) {},
    rejectApprovalPathSuccess() {},
    rejectApprovalPathFail(state, { payload: error }: PayloadAction<string>) {
      state.error = error;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    saveMatchRecord(state, action: PayloadAction<SaveMatchRecordPayloadProps>) {
      state.isLoadingRecord = true;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    saveMatchRecordSuccess(state, action: PayloadAction<unknown>) {
      state.isLoadingRecord = false;
    },
    saveMatchRecordFail(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.isLoadingRecord = false;
    },
  },
});

const {
  actions,
  reducer,
  selectors: { getMatchRecord },
} = MatchRecordSlice;

export const selectors = {
  selectMatchRecord: createSelector([selectAppState], getMatchRecord),
};

export const {
  approveApprovalPath,
  approveApprovalPathSuccess,
  approveApprovalPathFail,
  fetchMatchInfo,
  fetchMatchInfoSuccess,
  fetchMatchInfoFail,
  fetchMatchRecord,
  fetchMatchRecordSuccess,
  fetchMatchRecordFail,
  matchAction,
  matchActionSuccess,
  matchActionFail,
  fetchMatchResume,
  fetchMatchResumeSuccess,
  fetchMatchResumeFail,
  fetchCredentials,
  fetchCredentialsSuccess,
  fetchCredentialsFail,
  fetchDetails,
  fetchDetailsSuccess,
  fetchDetailsFail,
  fetchAttachments,
  fetchAttachmentsSuccess,
  fetchAttachmentsFail,
  downloadAllAttachments,
  downloadAllAttachmentsSuccess,
  downloadAllAttachmentsFail,
  rejectApprovalPath,
  rejectApprovalPathSuccess,
  rejectApprovalPathFail,
  saveMatchRecord,
  saveMatchRecordFail,
  saveMatchRecordSuccess,
} = actions;

export default reducer;
